import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, type LoginInput } from "@purlink/shared/auth";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AuthLayout, FormError, PasswordInput } from "~/components/auth/auth-layout";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { authApi, errorMessage, refreshMe } from "~/lib/auth";

export const Route = createFileRoute("/_auth/login")({
  validateSearch: z.object({
    redirect: z.string().startsWith("/").optional().catch(undefined),
    reason: z.enum(["signed-out", "reset", "deleted"]).optional().catch(undefined),
  }),
  component: LoginPage,
});

const notices = {
  "signed-out": "You've been signed out.",
  reset: "Your password has been updated. Sign in with your new password.",
  deleted: "Your account has been deleted.",
} as const;

function LoginPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const result = await authApi.signIn(values.email.trim().toLowerCase(), values.password);
      if (result.twoFactorRedirect) {
        await navigate({
          to: "/two-factor",
          search: { method: result.twoFactorMethods[0] ?? "totp", redirect: search.redirect },
        });
        return;
      }
      const me = await refreshMe(queryClient);
      await navigate({
        to: me?.onboarded ? (search.redirect ?? "/dashboard") : "/onboarding/security",
      });
    } catch (error) {
      form.setError("root.server", { message: errorMessage(error) });
    }
  });

  return (
    <AuthLayout
      title="Sign in"
      description="Welcome back to Purlink."
      footer={<span>Purlink is invite-only. Ask your administrator for access.</span>}
    >
      {search.reason && (
        <output className="block rounded-md bg-primary-soft px-3.5 py-3 text-body text-primary-soft-fg">
          {notices[search.reason]}
        </output>
      )}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" autoComplete="username" autoFocus {...form.register("email")} />
        </Field>
        <Field label="Password" error={errors.password?.message}>
          <PasswordInput autoComplete="current-password" {...form.register("password")} />
        </Field>
        <div className="-mt-1 flex justify-end">
          <Link
            to="/forgot-password"
            className="text-small font-medium text-primary hover:underline"
          >
            Forgot password?
          </Link>
        </div>
        <FormError message={errors.root?.server?.message} />
        <Button type="submit" size="lg" loading={isSubmitting}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
